import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { BlogSubscribe } from '../components/BlogSubscribe';
import { PenSquare, ArrowRight, Loader2 } from 'lucide-react';

const CANONICAL = 'https://k9os.app/blog';
const TITLE = 'K9os Blog — Dog Training Software, CRM & POS Insights for Dog Trainers';
const DESC = 'Practical playbooks, comparisons, and how-tos for dog trainers, board-and-train facilities, and dog training companies running on K9os App.';

const setMeta = (name, content, attr = 'name') => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};
const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    setMeta('description', DESC);
    setMeta('og:title', TITLE, 'property');
    setMeta('og:description', DESC, 'property');
    setMeta('og:url', CANONICAL, 'property');
    setMeta('twitter:title', TITLE);
    setMeta('twitter:description', DESC);
    setCanonical(CANONICAL);
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/blog?limit=50');
        if (alive) setPosts(data.posts || []);
      } catch {
        if (alive) setPosts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className='min-h-screen bg-white dark:bg-slate-950' data-testid='blog-index'>
      <PublicNav />

      {/* Hero */}
      <section className='bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 border-b border-slate-200 dark:border-slate-800'>
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/10 dark:text-[#93C5FD] text-xs font-semibold uppercase tracking-[0.18em]'>
            <PenSquare className='w-3.5 h-3.5' /> K9os Blog
          </div>
          <h1 className='mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight max-w-3xl'>
            Dog training software insights &mdash; written for working dog trainers
          </h1>
          <p className='mt-5 text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl'>
            Playbooks, comparisons, and how-tos on running a modern dog training business on one system &mdash;
            covering CRM, POS, memberships, board-and-train workflows, and marketing.
          </p>
        </div>
      </section>

      {/* Post grid */}
      <section className='py-12 md:py-16'>
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8'>
          {loading ? (
            <div className='py-20 flex items-center justify-center text-slate-500 gap-2'>
              <Loader2 className='w-5 h-5 animate-spin' /> Loading posts&hellip;
            </div>
          ) : posts.length === 0 ? (
            <div className='py-16 text-center' data-testid='blog-empty'>
              <PenSquare className='w-10 h-10 mx-auto text-slate-300 dark:text-slate-700 mb-3' />
              <h2 className='font-display text-xl font-bold text-[#0B1220] dark:text-slate-50'>New posts coming soon</h2>
              <p className='mt-2 text-slate-600 dark:text-slate-300'>We&rsquo;re publishing dog-trainer playbooks every week. Check back or {' '}
                <Link to='/contact' className='text-[#C8102E] font-semibold hover:underline'>book a demo</Link>.
              </p>
            </div>
          ) : (
            <ul className='grid grid-cols-1 md:grid-cols-2 gap-6' data-testid='blog-list'>
              {posts.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className='group block rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all'
                    data-testid={`blog-card-${p.slug}`}
                  >
                    {p.cover_image ? (
                      <div className='aspect-[16/9] bg-slate-100 dark:bg-slate-800 overflow-hidden'>
                        <img src={p.cover_image} alt={p.title} className='w-full h-full object-cover group-hover:scale-105 transition-transform' loading='lazy' />
                      </div>
                    ) : (
                      <div className='aspect-[16/9] bg-gradient-to-br from-[#1B3A87]/10 via-white to-[#C8102E]/10 dark:from-[#1B3A87]/25 dark:via-slate-900 dark:to-[#C8102E]/25 flex items-center justify-center'>
                        <PenSquare className='w-10 h-10 text-slate-300 dark:text-slate-700' />
                      </div>
                    )}
                    <div className='p-5'>
                      <div className='text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400'>{formatDate(p.published_at)}</div>
                      <h2 className='mt-1 font-display text-lg md:text-xl font-bold text-[#0B1220] dark:text-slate-50 leading-snug group-hover:text-[#1B3A87] dark:group-hover:text-[#93C5FD]'>{p.title}</h2>
                      {p.excerpt && <p className='mt-2 text-sm text-slate-600 dark:text-slate-300 line-clamp-3'>{p.excerpt}</p>}
                      <div className='mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#C8102E]'>Read post <ArrowRight className='w-3.5 h-3.5' /></div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/* Subscribe form — always visible below the post grid so
              even empty-state visitors can opt in. */}
          <div className='mt-12'>
            <BlogSubscribe source='blog-index' />
          </div>
        </div>
      </section>

      <MessageGregBubble />
    </div>
  );
}
